import { fetcher } from '@commenda/utils';
import {
  getSignedS3PutUrl,
  pushFileToS3Url,
} from '@commenda/utils/files/uploadFile';
import { LegacyDocumentType } from '@prisma/client';
import { useCallback, useRef, useState } from 'react';

interface IUploadState {
  isUploading: boolean;
  totalFiles: number;
  completedFiles: number;
}

interface IUseUploadCapTableDocsParams {
  corporationId: number;
  companyId: number | undefined;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
}

interface IUploadSuccessModalState {
  show: boolean;
  fiscalYear: number;
}

/**
 * Ownership-tab upload primitive. For each selected file:
 *   1. Get a signed S3 PUT URL
 *   2. PUT the file to S3
 *   3. POST file/upsert with documentType=CapTable and no folderId — the
 *      BE auto-routes Cap-table documents into the corp's
 *      `Financial Documents > FY <year> > Cap table` folder.
 *
 * Folder uploads (webkitdirectory) flatten — we don't mirror folder
 * structure since the Ownership tab doesn't expose folders. All files
 * land at the same routed destination.
 *
 * Owns the file/folder input refs, the post-upload success-modal state,
 * and the change handler so callers just spread the returned bits onto a
 * button + two hidden inputs.
 */
export function useUploadCapTableDocs({
  corporationId,
  companyId,
  onSuccess,
  onError,
}: IUseUploadCapTableDocsParams) {
  const [uploadState, setUploadState] = useState<IUploadState>({
    isUploading: false,
    totalFiles: 0,
    completedFiles: 0,
  });
  const [uploadSuccess, setUploadSuccess] =
    useState<IUploadSuccessModalState | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const uploadSingleFile = useCallback(
    async (file: File): Promise<boolean> => {
      try {
        const urlResponse = await getSignedS3PutUrl(
          file.name,
          file.type,
          companyId,
        );
        if (!urlResponse) return false;

        const uploadResult = await pushFileToS3Url(urlResponse.signedUrl, file);
        if (!uploadResult.ok) return false;

        await fetcher('file/upsert', {
          method: 'POST',
          body: {
            fileName: file.name,
            fileUrl: urlResponse.url,
            documentType: LegacyDocumentType.CapTable,
            corporationId,
          },
        });
        return true;
      } catch (err) {
        console.error(`Failed to upload ${file.name}:`, err);
        return false;
      }
    },
    [corporationId, companyId],
  );

  const uploadFiles = useCallback(
    async (files: FileList) => {
      const arr = Array.from(files);
      if (arr.length === 0) return;

      setUploadState({
        isUploading: true,
        totalFiles: arr.length,
        completedFiles: 0,
      });

      const failedNames: string[] = [];
      for (const file of arr) {
        const ok = await uploadSingleFile(file);
        if (!ok) failedNames.push(file.name);
        setUploadState((prev) => ({
          ...prev,
          completedFiles: prev.completedFiles + 1,
        }));
      }

      setUploadState({ isUploading: false, totalFiles: 0, completedFiles: 0 });

      const totalFiles = arr.length;
      if (failedNames.length === 0) {
        setUploadSuccess({ show: true, fiscalYear: new Date().getFullYear() });
        onSuccess?.(
          totalFiles === 1
            ? '1 document uploaded.'
            : `${totalFiles} documents uploaded.`,
        );
      } else {
        onError?.(
          `Upload failed for ${failedNames.length} of ${totalFiles}: ${failedNames.join(', ')}`,
        );
      }
    },
    [uploadSingleFile, onSuccess, onError],
  );

  const handleFilesPicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      void uploadFiles(e.target.files);
    }
    // Reset so picking the same files again still fires onChange.
    e.target.value = '';
  };

  return {
    uploadState,
    uploadSuccess,
    dismissUploadSuccess: () => setUploadSuccess(null),
    fileInputRef,
    folderInputRef,
    openFilePicker: () => fileInputRef.current?.click(),
    openFolderPicker: () => folderInputRef.current?.click(),
    handleFilesPicked,
  };
}
